import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";

import usePopover from "components/common/usePopover";
import EventService from "service/event/EventService";
import AppointmentService from "modules/appointments/service/AppointmentService";
import {
  MODES,
  MODES_WITH_AUTO_SP,
  RESCHEDULE_OTHER_REASON,
  SERVICE_SUB_TYPE,
  SERVICE_TYPE,
} from "modules/appointments/helper/appointmentConstants";
import "../styles/reschedule.scss";

/**
 * Reschedule an appointment — bridge's flow and content, in phr's design.
 *
 * SOURCE OF TRUTH: bridge's navigo-module DocumentUpload.js, the dialog titled
 * "Reschedule appointment?". Every string, every field and the order they come
 * in, what each control clears and refetches (bridge's resetValues 1/2/7 and
 * the checkbox handler), when each field is disabled (bridge's `loading`), the
 * booking window (getMinDate / getMaxDate), the ranged slot cache and its
 * per-date states, the click-time validation and the move itself are ported
 * from there. Only the LOOK is phr's. It is opened by useRescheduleFlow, which
 * runs bridge's "Update missing profile details" step first.
 *
 * WHAT GOES ON THE WIRE, each bridge's:
 *   · GET  /v1/appointment/reschedule-reasons — chips, verbatim, "Other reason"
 *     appended; the pick is sent as `reason` (stored on the appointment and in
 *     its audit — api-appointment-service release, request_response.go:956).
 *   · POST .../provider/language/options — consultations that are not in-person.
 *   · POST .../provider/patient — on open (the appointment's own provider is
 *     preselected) and again on every Language change.
 *   · POST /v1/appointment/available-slots-range-bridge — the 7-day strip in
 *     one request, a single date on retry or on a calendar pick.
 *   · GET/POST /v1/appointment/booking/config/... and GET /v1/vendor/priority —
 *     the booking window for avail-care consultations and home-sample services.
 *   · POST /v1/appointment/reschedule — { appointmentID, appointmentDate,
 *     timeslot, timeSlotId, serviceProviderID, reason }.
 *
 * DELIBERATE DIFFERENCES FROM BRIDGE (approved, or not reachable in phr):
 *   · memberPatientId on the slots call is the member (bookedFor || patientID).
 *   · the move is postAgain (no automatic re-fire on a non-2xx).
 *   · provider rows with id 0 are never offered.
 *   · with auto-select on, a slot that names no provider is refused rather than
 *     posting serviceProviderID null.
 *   · a provider response superseded by a newer request is dropped.
 *   · selectL2Availability is not sent (slot-restriction dashboard only).
 *   · the cart-appointment dialog is not ported (product decision).
 *   · the health-checkup / vaccine LOCATION block (Google maps / Manual / Saved
 *     addresses, state/city/locality, area radius) is not ported — see the
 *     2026-09-10 handover. Those appointments get the type-1 provider list.
 *   · the reason chips, Language options and booking windows are fetched when
 *     the dialog opens — bridge fetches them with its page, which phr lacks.
 *   · AUTO-SELECT HAS BOOKING WINDOWS (2026-09-14). Bridge gives auto-select
 *     "today, no end", so days outside every provider's window stayed open and
 *     the calendar went anywhere, while a CHOSEN provider's out-of-window days are
 *     disabled — the two modes looked different for the same dates. Auto-select
 *     now treats a day as bookable when ANY offered provider's own window covers
 *     it (a gap between two providers' windows stays disabled), strip and
 *     calendar alike — exactly as a chosen provider uses its own. The server
 *     already only picks, per date, a provider whose window covers it
 *     (appointment_slot_range_bridge.go), so this only changes what is drawn,
 *     never what can be booked.
 *   · A CALENDAR PICK DOES NOT PIN THE STRIP OUTSIDE THE WINDOW. Bridge keeps the
 *     strip's anchor across the auto-select toggle, so a date picked in auto mode
 *     left a chosen provider looking at seven disabled days while their bookable
 *     days were off the strip. An anchor the current window cannot book is
 *     ignored, and a chosen date the window moves past (a booking window that
 *     loads late) is cleared with its slot.
 *   · A DAY WITH NO FREE SLOTS IS DISABLED, like a day outside the window (bridge
 *     leaves it clickable, to show "No slots available on this date"). A day
 *     whose slots failed to load stays clickable, to retry.
 *   · "YES, RESCHEDULE" IS DISABLED until the move is complete — a reason (and
 *     its comment for "Other reason"), a provider or auto-select, a bookable date
 *     and a slot — where bridge enables it always and toasts what is missing on
 *     click. The next missing step is written under the form (and is the button's
 *     description); the click-time checks stay behind it.
 */

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MON = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];
const MONTH_FULL = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const APOLLO_VENDOR_CODE = "ApolloClinic";

const pad = (n) => String(n).padStart(2, "0");
/** `a ?? b` — spelled out, as the rest of phr is. */
const nz = (v, d) => (v === null || v === undefined ? d : v);
/** Local Y-M-D key — never toISOString(), which would shift the day off-UTC. */
const toKey = (d) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const startOfToday = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};
const addDays = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const keyToDate = (k) => {
  const [y, m, d] = k.split("-").map(Number);
  return new Date(y, m - 1, d);
};
const toast = (message) => EventService.emit("showError", { message });
/** Bridge's `Error: ${err?.message}` — "Error: undefined" included. */
const toastErr = (err) => toast(`Error: ${err === null || err === undefined ? undefined : err.message}`);

/** "Tue, 24 Feb 2026" — bridge's "ddd, D MMM YYYY" (success line). */
const longWithDow = (key) => {
  const d = keyToDate(key);
  return `${DOW[d.getDay()]}, ${d.getDate()} ${MON[d.getMonth()]} ${d.getFullYear()}`;
};
/** "Tue, 24 Feb" — bridge's "ddd, D MMM" (the "Available slots ·" heading). */
const shortWithDow = (key) => {
  const d = keyToDate(key);
  return `${DOW[d.getDay()]}, ${d.getDate()} ${MON[d.getMonth()]}`;
};

/**
 * "07 Sep 2026, 10:20 - 11:00 AM" — bridge's "Currently scheduled" line:
 * zero-padded hours, the meridiem on the END only, both when the booking spans
 * 12 hours or more, and a single "hh:mm A" when there is no duration.
 * Returns "" for a missing / invalid / epoch-1970 date, and the caller then
 * hides the whole line instead of bridge's dangling "Currently scheduled · ".
 */
const currentlyScheduled = (appointmentDate, timeSlot, duration) => {
  const iso = String(appointmentDate || "");
  const day = iso.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day) || day.startsWith("1970")) return "";
  const d = keyToDate(day);
  const dateText = `${pad(d.getDate())} ${MON[d.getMonth()]} ${d.getFullYear()}`;

  const start = Number(timeSlot) || 0;
  const hhmm = (mins) => {
    const h24 = Math.floor(mins / 60) % 24;
    const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
    return `${pad(h12)}:${pad(mins % 60)}`;
  };
  const mer = (mins) => (Math.floor(mins / 60) % 24 < 12 ? "AM" : "PM");
  let timeText;
  if (!duration || duration <= 0) timeText = `${hhmm(start)} ${mer(start)}`;
  else if (duration >= 720)
    timeText = `${hhmm(start)} ${mer(start)} - ${hhmm(start + duration)} ${mer(start + duration)}`;
  else timeText = `${hhmm(start)} - ${hhmm(start + duration)} ${mer(start + duration)}`;
  return `${dateText}, ${timeText}`;
};

/**
 * Bridge's formatSlotRange + slotLabel. The server's `label` wins when it is
 * non-empty; otherwise "h:mm A" (no duration, or <= 0), "h:mm A - h:mm A"
 * (>= 12 hours), else "h:mm - h:mm A" — the END meridiem only, even across noon
 * ("11:30 - 12:00 PM"). Hours are NOT zero-padded here, unlike the strip above.
 * A missing start reads as midnight, as moment's startOf("day") does.
 */
const slotLabelFor = (duration) => {
  const hm = (mins, withMeridiem) => {
    const m = ((Math.floor(mins) % 1440) + 1440) % 1440;
    const h24 = Math.floor(m / 60);
    const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
    const t = `${h12}:${pad(m % 60)}`;
    return withMeridiem ? `${t} ${h24 < 12 ? "AM" : "PM"}` : t;
  };
  const range = (startMinutes) => {
    const start = Number(startMinutes) || 0;
    if (duration === null || duration === undefined || duration <= 0) return hm(start, true);
    return duration >= 720
      ? `${hm(start, true)} - ${hm(start + duration, true)}`
      : `${hm(start, false)} - ${hm(start + duration, true)}`;
  };
  return (slot) =>
    slot && slot.label !== null && slot.label !== undefined && slot.label !== ""
      ? slot.label
      : range(slot && slot.value);
};

const CheckMini = () => (
  <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.8"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M2 6.2l2.6 2.6L10 3" />
  </svg>
);
const CalendarIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3.5" y="5" width="17" height="15.5" rx="2.5" />
    <path d="M3.5 9.5h17M8 3v4M16 3v4" />
  </svg>
);
const CheckBig = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12.5l4.2 4.2L19 7" />
  </svg>
);
const Spinner = ({ large }) => (
  <span className={`rs-spin${large ? " is-lg" : ""}`} aria-hidden="true" />
);
Spinner.propTypes = { large: PropTypes.bool };

/** The margin-level dot bridge draws beside a provider (margin.colour). */
const MarginDot = ({ provider }) =>
  provider && provider.margin && provider.margin.colour ? (
    <span className="rs-pick-dot" style={{ backgroundColor: provider.margin.colour }} />
  ) : null;
MarginDot.propTypes = { provider: PropTypes.object };

/**
 * The strip's trailing calendar button + its picker — bridge's DatePicker
 * (dialog variant, autoOk): a toolbar with the year and "ddd, MMM D", a
 * "MMMM YYYY" month header, Sunday-first weekday names, and Cancel / OK.
 * Clicking a day picks it at once; OK picks the date the toolbar shows.
 * Bounded by the booking window on both ends.
 */
const StripCalendar = ({ minKey, maxKey, isDayBookable, selectedKey, disabled, onPick, onOpenChange }) => {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const { triggerRef, menuRef, menuStyle } = usePopover(open, close);

  useEffect(() => {
    if (onOpenChange) onOpenChange(open);
  }, [open, onOpenChange]);

  /* MUI shows the picker's value, or today when it has none — held inside the
     window so OK can never pick a day the grid disables. */
  const focusKey = useMemo(() => {
    let k = selectedKey || toKey(startOfToday());
    if (k < minKey) k = minKey;
    if (maxKey && k > maxKey) k = maxKey;
    return k;
  }, [selectedKey, minKey, maxKey]);
  const focus = keyToDate(focusKey);

  const [view, setView] = useState(() => new Date(focus.getFullYear(), focus.getMonth(), 1));
  useEffect(() => {
    if (open) setView(new Date(focus.getFullYear(), focus.getMonth(), 1));
    // Re-centre on each open only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const min = keyToDate(minKey);
  const max = maxKey ? keyToDate(maxKey) : null;
  const grid = useMemo(() => {
    const first = new Date(view.getFullYear(), view.getMonth(), 1);
    const lead = first.getDay(); // Sunday-first, as moment.weekdaysShort
    const days = new Date(view.getFullYear(), view.getMonth() + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < lead; i += 1) cells.push(null);
    for (let d = 1; d <= days; d += 1) {
      cells.push(new Date(view.getFullYear(), view.getMonth(), d));
    }
    return cells;
  }, [view]);

  const canPrev =
    new Date(view.getFullYear(), view.getMonth(), 1) >
    new Date(min.getFullYear(), min.getMonth(), 1);
  const canNext = !max || new Date(view.getFullYear(), view.getMonth() + 1, 1) <= max;
  const pick = (key) => {
    setOpen(false);
    onPick(key);
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="rs-day rs-day-cal"
        title="Choose another date"
        aria-label="Choose another date"
        aria-haspopup="dialog"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
      >
        <CalendarIcon />
      </button>

      {open &&
        ReactDOM.createPortal(
          <div
            ref={menuRef}
            className="rs-cal"
            style={{ top: menuStyle.top, left: menuStyle.left }}
            role="dialog"
            aria-label="Pick a date"
          >
            <div className="rs-cal-bar">
              <span className="rs-cal-year">{focus.getFullYear()}</span>
              <span className="rs-cal-date">
                {`${DOW[focus.getDay()]}, ${MON[focus.getMonth()]} ${focus.getDate()}`}
              </span>
            </div>
            <div className="rs-cal-head">
              <button
                type="button"
                className="rs-cal-nav"
                disabled={!canPrev}
                aria-label="Previous month"
                onClick={() => setView(new Date(view.getFullYear(), view.getMonth() - 1, 1))}
              >
                &#8249;
              </button>
              <span className="rs-cal-title">
                {MONTH_FULL[view.getMonth()]} {view.getFullYear()}
              </span>
              <button
                type="button"
                className="rs-cal-nav"
                disabled={!canNext}
                aria-label="Next month"
                onClick={() => setView(new Date(view.getFullYear(), view.getMonth() + 1, 1))}
              >
                &#8250;
              </button>
            </div>
            <div className="rs-cal-grid">
              {DOW.map((d) => (
                <span className="rs-cal-dow" key={d}>{d}</span>
              ))}
              {grid.map((d, i) => {
                if (!d) return <span key={`pad-${i}`} className="rs-cal-pad" />;
                const key = toKey(d);
                const ok = d >= min && (!max || d <= max) && (!isDayBookable || isDayBookable(key));
                return (
                  <button
                    key={key}
                    type="button"
                    className={`rs-cal-day${key === selectedKey ? " is-selected" : ""}`}
                    disabled={!ok}
                    onClick={() => pick(key)}
                  >
                    {d.getDate()}
                  </button>
                );
              })}
            </div>
            <div className="rs-cal-actions">
              <button type="button" className="rs-cal-act" onClick={() => setOpen(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="rs-cal-act"
                disabled={Boolean(isDayBookable) && !isDayBookable(focusKey)}
                onClick={() => pick(focusKey)}
              >
                OK
              </button>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};
StripCalendar.propTypes = {
  minKey: PropTypes.string.isRequired,
  maxKey: PropTypes.string,
  /** Auto-select's per-provider windows: a day inside [min, max] may still be unbookable. */
  isDayBookable: PropTypes.func,
  selectedKey: PropTypes.string,
  disabled: PropTypes.bool,
  onPick: PropTypes.func.isRequired,
  onOpenChange: PropTypes.func,
};

/**
 * The service-provider field — bridge's single MUI Autocomplete: the field is
 * the search box (placeholder "Select"), the query is matched lower-cased and
 * UNtrimmed against the label, an empty match reads "No options", the chosen
 * provider can be cleared (x, title "Clear") and erasing the text clears it
 * too, the popup button reads "Open"/"Close", and a margin-colour dot sits
 * beside each provider that has one — in the list and in the field. It stays
 * enabled with an empty list; `disabled` is bridge's rule, passed in.
 */
const ProviderPicker = ({
  id, options, value, disabled, getLabel, getSearch, onChange, onOpenChange,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [typed, setTyped] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const close = useCallback(() => setOpen(false), []);

  const shown = useMemo(() => {
    if (!typed) return options;
    const q = query.toLowerCase();
    return options.filter((o) => getSearch(o).toLowerCase().includes(q));
  }, [options, query, typed, getSearch]);
  const { triggerRef, menuRef, menuStyle } = usePopover(open, close, shown.length);

  useEffect(() => {
    if (onOpenChange) onOpenChange(open);
    if (!open) {
      setQuery("");
      setTyped(false);
    } else {
      const i = value ? shown.indexOf(value) : -1;
      setActive(i >= 0 ? i : 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const commit = (opt) => {
    setOpen(false);
    if (opt !== value) onChange(opt);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) setOpen(true);
      else setActive((a) => Math.min(a + 1, shown.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter" && open && shown[active]) {
      e.preventDefault();
      commit(shown[active]);
    }
  };

  return (
    <div ref={triggerRef} className={`rs-pick${disabled ? " is-disabled" : ""}`}>
      <MarginDot provider={value} />
      <input
        id={id}
        ref={inputRef}
        type="text"
        className="rs-pick-input"
        placeholder="Select"
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-controls={id ? `${id}-menu` : undefined}
        disabled={disabled}
        value={open && typed ? query : value ? getLabel(value) : ""}
        onClick={() => !disabled && setOpen(true)}
        onChange={(e) => {
          setTyped(true);
          setQuery(e.target.value);
          if (!open) setOpen(true);
          // MUI: emptying the text of a clearable field clears its value.
          if (e.target.value === "" && value != null) onChange(null);
        }}
        onKeyDown={onKeyDown}
      />
      {value != null && !disabled && (
        <button
          type="button"
          className="rs-pick-clear"
          title="Clear"
          aria-label="Clear"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            setOpen(false);
            onChange(null);
          }}
        >
          &times;
        </button>
      )}
      <button
        type="button"
        className="rs-pick-caret"
        title={open ? "Close" : "Open"}
        aria-label={open ? "Close" : "Open"}
        disabled={disabled}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => {
          setOpen((o) => !o);
          if (inputRef.current) inputRef.current.focus();
        }}
      >
        &#9662;
      </button>

      {open &&
        ReactDOM.createPortal(
          <ul
            id={id ? `${id}-menu` : undefined}
            ref={menuRef}
            className="rs-pmenu"
            role="listbox"
            style={{ ...menuStyle, maxHeight: Math.min(menuStyle.maxHeight || 320, 320) }}
          >
            {shown.length === 0 ? (
              <li className="rs-pmenu-empty">No options</li>
            ) : (
              shown.map((opt, i) => (
                <li key={`${opt.id}-${i}`}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={opt === value}
                    className={`rs-popt${opt === value ? " is-selected" : ""}${
                      i === active ? " is-active" : ""
                    }`}
                    onMouseDown={(e) => e.preventDefault()}
                    onMouseEnter={() => setActive(i)}
                    onClick={() => commit(opt)}
                  >
                    <MarginDot provider={opt} />
                    <span>{getLabel(opt)}</span>
                  </button>
                </li>
              ))
            )}
          </ul>,
          document.body
        )}
    </div>
  );
};
ProviderPicker.propTypes = {
  id: PropTypes.string,
  options: PropTypes.array.isRequired,
  value: PropTypes.object,
  disabled: PropTypes.bool,
  getLabel: PropTypes.func.isRequired,
  getSearch: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onOpenChange: PropTypes.func,
};

/**
 * The Language field — bridge's MUI Autocomplete `multiple`: picks are chips
 * you can delete one by one (or all at once, "Clear"), the field itself is the
 * search box (placeholder "Select"), the query is matched lower-cased and
 * untrimmed, an empty match reads "No options", picking closes the list, and
 * Backspace in an empty box removes the last chip. Never disabled.
 */
const LanguageMulti = ({ options, value, onChange, onOpenChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const inputRef = useRef(null);
  const close = useCallback(() => setOpen(false), []);
  const shown = useMemo(() => {
    const q = query.toLowerCase();
    return q ? options.filter((o) => String(o).toLowerCase().includes(q)) : options;
  }, [options, query]);
  const { triggerRef, menuRef, menuStyle } = usePopover(open, close, shown.length);

  useEffect(() => {
    if (onOpenChange) onOpenChange(open);
    if (!open) setQuery("");
  }, [open, onOpenChange]);

  const toggle = (lang) => {
    onChange(value.indexOf(lang) === -1 ? value.concat([lang]) : value.filter((l) => l !== lang));
    setOpen(false);
  };

  return (
    <div
      ref={triggerRef}
      className="rs-tags"
      onMouseDown={(e) => {
        if (e.target === inputRef.current) return;
        e.preventDefault();
        if (inputRef.current) inputRef.current.focus();
        setOpen(true);
      }}
    >
      {value.map((lang) => (
        <span className="rs-tag" key={lang}>
          {lang}
          <button
            type="button"
            className="rs-tag-x"
            aria-label={`Remove ${lang}`}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={() => onChange(value.filter((l) => l !== lang))}
          >
            &times;
          </button>
        </span>
      ))}
      <input
        id="rs-language"
        ref={inputRef}
        type="text"
        className="rs-tags-input"
        placeholder="Select"
        autoComplete="off"
        value={query}
        onClick={() => setOpen(true)}
        onChange={(e) => {
          setQuery(e.target.value);
          if (!open) setOpen(true);
        }}
        onKeyDown={(e) => {
          if (e.key === "Backspace" && query === "" && value.length) {
            onChange(value.slice(0, -1));
          }
        }}
      />
      {value.length > 0 && (
        <button
          type="button"
          className="rs-pick-clear"
          title="Clear"
          aria-label="Clear"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={() => onChange([])}
        >
          &times;
        </button>
      )}
      <button
        type="button"
        className="rs-pick-caret"
        title={open ? "Close" : "Open"}
        aria-label={open ? "Close" : "Open"}
        onMouseDown={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
        onClick={() => setOpen((o) => !o)}
      >
        &#9662;
      </button>
      {open &&
        ReactDOM.createPortal(
          <ul
            ref={menuRef}
            className="rs-pmenu"
            role="listbox"
            aria-multiselectable="true"
            style={{ ...menuStyle, maxHeight: Math.min(menuStyle.maxHeight || 280, 280) }}
          >
            {shown.length === 0 ? (
              <li className="rs-pmenu-empty">No options</li>
            ) : (
              shown.map((lang, i) => {
                const on = value.indexOf(lang) !== -1;
                return (
                  <li key={`${lang}-${i}`}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={on}
                      className={`rs-popt${on ? " is-selected" : ""}`}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => toggle(lang)}
                    >
                      <span>{lang}</span>
                    </button>
                  </li>
                );
              })
            )}
          </ul>,
          document.body
        )}
    </div>
  );
};
LanguageMulti.propTypes = {
  options: PropTypes.array.isRequired,
  value: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  onOpenChange: PropTypes.func,
};

const RescheduleModal = ({ appointment, onClose, onRescheduled }) => {
  const service = useRef(new AppointmentService()).current;
  const mounted = useRef(true);
  const panelRef = useRef(null);
  const lastFocused = useRef(null);
  /* Any child popover that is open — calendar, Language, provider — so Escape
     and the backdrop dismiss that popover alone, not the whole dialog. */
  const popoverOpenRef = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // ── what kind of appointment this is (bridge's branches) ────────────
  const mode = appointment.mode;
  const typeId = appointment.serviceTypeID;
  const isConsult = typeId === SERVICE_TYPE.CONSULTATION;
  const isLab = typeId === SERVICE_TYPE.HEALTH_CHECKUP || typeId === SERVICE_TYPE.VACCINE;
  const isHomeSample = mode === MODES.HOME_SAMPLE;
  const isAvailCare = isConsult && appointment.serviceSubtypeID === SERVICE_SUB_TYPE.AVAIL_CARE;
  const showLanguage = isConsult && mode !== MODES.IN_PERSON_CONSULTATION;
  /* Bridge renders NO checkbox and NO provider field for these; the provider
     preselected by the first fetch is what the move is sent with. */
  const hideProviderBlock =
    (isConsult && mode === MODES.IN_PERSON_CONSULTATION) ||
    (isLab &&
      (isHomeSample ||
        (mode === MODES.CENTRE_VISIT &&
          appointment.serviceProviderGroupVendorName === APOLLO_VENDOR_CODE)));

  // ── reason ───────────────────────────────────────────────────────────
  const [reasons, setReasons] = useState([]);
  const [reason, setReason] = useState(null);
  const [comment, setComment] = useState("");

  // ── language / provider ─────────────────────────────────────────────
  const [languages, setLanguages] = useState([]);
  const [languageSelected, setLanguageSelected] = useState([]);
  const [providers, setProviders] = useState([]);
  const [provider, setProvider] = useState(null);
  const [autoPick, setAutoPick] = useState(false);
  const [providersLoading, setProvidersLoading] = useState(false);

  // ── booking-window configs (bridge loads these with its page) ────────
  const [availCare, setAvailCare] = useState({ min: null, max: null });
  const [homeSample, setHomeSample] = useState({ min: null, max: null });
  const [priorityIds, setPriorityIds] = useState([]);

  // ── date / slots ────────────────────────────────────────────────────
  const [anchorKey, setAnchorKey] = useState(null);
  /* { key, seq } — a NEW object on every pick, as bridge's setDateSelected(
     moment(day)) is, so re-picking the chosen day still clears the slot. */
  const [dateSel, setDateSel] = useState(null);
  const dateSeq = useRef(0);
  const selectedDate = dateSel ? dateSel.key : null;
  const [selectedSlot, setSelectedSlot] = useState(null);
  /* { [dateKey]: { loading, error, slots, serviceProviderId } } — bridge's
     slotFetchState, keyed to one provider/language combination. */
  const [slotState, setSlotState] = useState({});
  const requestKeyRef = useRef("");

  // ── the move ─────────────────────────────────────────────────────────
  const [busy, setBusy] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  /* Closed while the move was in flight: bridge lets you, and still shows the
     success dialog (or the error toast) when the response lands. */
  const [dismissed, setDismissed] = useState(false);
  const dismissedRef = useRef(false);

  /* Bridge's page-wide `loading`: true while providers load and while the move
     is in flight. It disables the checkbox, the provider field, the calendar
     and "Yes, reschedule" — not the day buttons, not "No, go back". */
  const loading = providersLoading || busy;
  const slotLabel = useMemo(() => slotLabelFor(appointment.serviceDuration), [appointment.serviceDuration]);

  // ── the booking window — bridge's getMinDate / getMaxDate ────────────
  const isProviderChosen = autoPick || provider != null;
  const usesGlobalBookingConfig = (p) => (p == null ? true : priorityIds.indexOf(p.id) !== -1);
  const today = startOfToday();
  const plus = (n) => toKey(addDays(today, Number(n) || 0));
  const plusMinusOne = (n) => toKey(addDays(today, (Number(n) || 0) - 1));

  let minKey;
  let maxKey;
  /* Auto-select only: each offered provider's own [min, max] — a day is bookable
     when any of them covers it. null in every other mode. */
  let autoWindows = null;
  if (isHomeSample) {
    if (typeId === SERVICE_TYPE.HEALTH_CHECKUP && !usesGlobalBookingConfig(provider)) {
      minKey = provider.minBookingDays === undefined ? toKey(today) : plus(provider.minBookingDays);
      maxKey = provider.maxBookingDays === undefined ? null : plusMinusOne(provider.maxBookingDays);
    } else {
      minKey = homeSample.min == null ? toKey(today) : plus(homeSample.min);
      maxKey = homeSample.max == null ? null : plusMinusOne(homeSample.max);
    }
  } else if (isAvailCare) {
    minKey = availCare.min == null ? toKey(today) : plus(availCare.min);
    maxKey = availCare.max == null ? null : plusMinusOne(availCare.max);
  } else if (autoPick) {
    /* The offered providers' own windows — see the header. A provider with no
       minBookingDays opens from today; one with no maxBookingDays leaves its end
       open, as a chosen provider's does. minKey/maxKey are the outer span. */
    autoWindows = (providers.length ? providers : [null]).map((p) => ({
      lo: !p || p.minBookingDays === undefined ? toKey(today) : plus(p.minBookingDays),
      hi: !p || p.maxBookingDays === undefined ? null : plusMinusOne(p.maxBookingDays),
    }));
    minKey = autoWindows.reduce((a, w) => (w.lo < a ? w.lo : a), autoWindows[0].lo);
    maxKey = autoWindows.some((w) => w.hi == null)
      ? null
      : autoWindows.reduce((a, w) => (w.hi > a ? w.hi : a), autoWindows[0].hi);
  } else {
    minKey = !provider || provider.minBookingDays === undefined ? toKey(today) : plus(provider.minBookingDays);
    maxKey = !provider || provider.maxBookingDays === undefined ? null : plusMinusOne(provider.maxBookingDays);
  }

  const isBookable = (key) =>
    key >= minKey &&
    (!maxKey || key <= maxKey) &&
    (!autoWindows || autoWindows.some((w) => key >= w.lo && (!w.hi || key <= w.hi)));

  const stripStartKey = (() => {
    if (!anchorKey || !isBookable(anchorKey)) return minKey;
    const back = toKey(addDays(keyToDate(anchorKey), -3));
    return back > minKey ? back : minKey;
  })();
  const stripDays = Array.from({ length: 7 }, (_, i) => {
    const d = addDays(keyToDate(stripStartKey), i);
    return { key: toKey(d), dow: DOW[d.getDay()], dom: String(d.getDate()) };
  });
  const stripKeys = stripDays.map((d) => d.key).join(",");

  const requestKey = `${autoPick ? "auto" : provider ? provider.id : "none"}|${languageSelected.join(",")}`;

  // ── fetches bridge makes with its page ──────────────────────────────
  useEffect(() => {
    service.getRescheduleReasons(
      (res) => {
        if (mounted.current) setReasons(Array.isArray(res) ? res : []);
      },
      (err) => {
        if (!mounted.current) return;
        setReasons([]);
        toastErr(err);
      }
    );
    if (showLanguage) {
      service.getProviderLanguages(
        { planId: appointment.planID, serviceId: appointment.serviceID, serviceMode: appointment.mode },
        (res) => {
          if (!mounted.current) return;
          setLanguages(
            res == null || res.languages == null || res.languages.length === 0 ? [] : res.languages
          );
        },
        (err) => {
          if (!mounted.current) return;
          setLanguages([]);
          toastErr(err);
        }
      );
    }
    if (isAvailCare) {
      service.getAvailCareDates(
        appointment.patientID,
        appointment.planID,
        (res) => {
          if (mounted.current && res != null) {
            setAvailCare({ min: res.minBookingDays, max: res.maxAdvBookingDays });
          }
        },
        (err) => {
          if (!mounted.current) return;
          setAvailCare({ min: null, max: null });
          toastErr(err);
        }
      );
    }
    if (isHomeSample) {
      const onWindow = (res) => {
        if (mounted.current && res != null) {
          setHomeSample({ min: res.minBookingDays, max: res.maxAdvBookingDays });
        }
      };
      const onWindowError = (err) => {
        if (!mounted.current) return;
        setHomeSample({ min: null, max: null });
        toastErr(err);
      };
      if (typeId === SERVICE_TYPE.VACCINE) {
        service.getBookingConfigByVendor(
          {
            patientID: appointment.patientID,
            serviceID: appointment.serviceID,
            planID: appointment.planID,
            vendorId: appointment.vendorId,
          },
          onWindow,
          onWindowError
        );
      } else {
        service.getHomeSampleDates(appointment.patientID, appointment.planID, onWindow, onWindowError);
      }
      if (typeId === SERVICE_TYPE.HEALTH_CHECKUP) {
        service.getPriorityVendors(
          (res) => {
            if (!mounted.current) return;
            setPriorityIds(
              res == null || res.length === 0 ? [] : res.map((v) => v.mappedServiceProviderId)
            );
          },
          (err) => {
            if (mounted.current) toastErr(err);
          }
        );
      }
    }
    // The dialog is keyed to one appointment for its lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── providers: bridge's getServiceProvider, type 1 then type 2 ──────
  const providerSeq = useRef(0);
  const firstProviderLoad = useRef(true);
  useEffect(() => {
    const isFirst = firstProviderLoad.current;
    firstProviderLoad.current = false;
    providerSeq.current += 1;
    const seq = providerSeq.current;
    const current = () => mounted.current && seq === providerSeq.current;
    setProvidersLoading(true);
    const body = {
      pageNum: 0,
      pageSize: 0,
      planId: appointment.planID,
      serviceId: appointment.serviceID,
      serviceMode: appointment.mode,
      patientId: appointment.patientID,
      languageMatch: nz(languageSelected, []),
      vendorCode: appointment.vendorCode,
      vendorDoctorId: appointment.vendorDoctorId,
      vendorId: appointment.vendorId,
      providerAddressId: nz(appointment.providerAddressId, ""),
      geolocationFilter: null,
      ...(appointment.serviceProviderGroupVendorName !== APOLLO_VENDOR_CODE && {
        excludedVendors: [APOLLO_VENDOR_CODE],
      }),
    };
    service.getRescheduleProviders(
      body,
      (res) => {
        if (!current()) return;
        /* Bridge renders the rows as they come; id-0 rows are dropped as in
           phr's create flow (the zero-price placeholder of an excluded vendor). */
        const list = nz(res && res.providers, []).filter((p) => p && p.id > 0);
        if (list.length > 0) {
          setProviders(list);
          if (isFirst) {
            const same = list.find((p) => p.id === appointment.serviceProviderID);
            setProvider(same || null);
            if (!same) toast("Unable to set the same service provider");
          }
          setProvidersLoading(false);
          return;
        }
        setProviders([]);
        setProvidersLoading(false);
        toast("No service provider found");
      },
      (err) => {
        if (!current()) return;
        // The previous rows stay, as bridge's do.
        toastErr(err);
        setProvidersLoading(false);
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [languageSelected]);

  // ── the ranged slot cache — bridge's fetchSlotsForDates ─────────────
  const fetchSlots = (keys) => {
    if (!keys.length) return;
    const key = requestKeyRef.current;
    const isCurrent = () => mounted.current && requestKeyRef.current === key;
    const sorted = [...keys].sort();
    const vendorId = provider && provider.vendorId ? provider.vendorId : nz(appointment.vendorId, "");
    const vendorCode = provider && provider.vendorCode ? provider.vendorCode : nz(appointment.vendorCode, "");
    const vendorDoctorId =
      provider && provider.vendorDoctorId ? provider.vendorDoctorId : nz(appointment.vendorDoctorId, 0);
    const body = {
      planId: appointment.planID,
      serviceId: appointment.serviceID,
      serviceProviderId: autoPick
        ? 0
        : provider == null || provider.id == null
        ? null
        : provider.id,
      bookingMode: appointment.mode,
      startDate: sorted[0],
      endDate: sorted[sorted.length - 1],
      addressId: appointment.addressId,
      vendor: vendorId,
      vendorCode,
      vendorDoctorId,
      languageMatch: nz(languageSelected, []),
      providerAddressId: nz(appointment.providerAddressId, ""),
      patientId: appointment.patientID,
      isReschedule: true,
      /* The MEMBER on a family booking — an approved difference, see above. */
      memberPatientId: appointment.bookedFor || appointment.patientID,
    };
    const markDates = (prev, entryFor) => {
      const next = { ...prev };
      keys.forEach((k) => {
        next[k] = entryFor(k);
      });
      return next;
    };
    setSlotState((prev) =>
      markDates(prev, () => ({ loading: true, error: null, slots: [], serviceProviderId: null }))
    );
    service.getAvailableSlotsRange(
      body,
      (res) => {
        if (!isCurrent()) return;
        const byDate = new Map();
        (res && Array.isArray(res.dates) ? res.dates : []).forEach((e) => {
          if (e && e.date != null) byDate.set(e.date, e);
        });
        const resolved = {};
        const messages = [];
        keys.forEach((k) => {
          const e = byDate.get(k);
          /* A date the response skipped is a failure for that date; and ANY
             non-null error — "" included — marks the date as failed. */
          const error = e == null ? "Slots were not returned for this date" : nz(e.error, null);
          if (error != null && messages.indexOf(error) === -1) messages.push(error);
          resolved[k] = {
            loading: false,
            error,
            slots: error == null && Array.isArray(e.slots) ? e.slots : [],
            serviceProviderId: nz(e && e.serviceProviderId, null),
          };
        });
        setSlotState((prev) => ({ ...prev, ...resolved }));
        // One toast per distinct failure, not per failing date.
        messages.forEach((m) => toast(`Error: ${m}`));
      },
      (err) => {
        if (!isCurrent()) return;
        const message = nz(err && err.message, "Something went wrong");
        toast(`Error: ${message}`);
        setSlotState((prev) =>
          markDates(prev, () => ({ loading: false, error: message, slots: [], serviceProviderId: null }))
        );
      }
    );
  };

  /* Bridge's three effects, in bridge's order — the order matters: a calendar
     pick outside the strip asks for that one date AND for the new strip. */
  useEffect(() => {
    if (dateSel == null) return;
    setSelectedSlot(null);
    if (slotState[dateSel.key] === undefined) fetchSlots([dateSel.key]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateSel]);

  useEffect(() => {
    requestKeyRef.current = requestKey;
    setSlotState({});
  }, [requestKey]);

  useEffect(() => {
    if (!isProviderChosen) return;
    fetchSlots(stripKeys.split(",").filter((k) => slotState[k] === undefined));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProviderChosen, requestKey, stripKeys, slotState]);

  /* A booking window that lands after a day was chosen (avail-care / home-sample
     configs load with the dialog) can move past it: drop the day and its slot
     rather than keep a selection the strip no longer shows. */
  const windowSig = `${minKey}|${maxKey}|${autoWindows ? JSON.stringify(autoWindows) : ""}`;
  useEffect(() => {
    if (selectedDate && !isBookable(selectedDate)) {
      setDateSel(null);
      setSelectedSlot(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowSig]);

  const selectedState = selectedDate ? slotState[selectedDate] : null;
  /* With auto-select on, the provider is whoever the chosen date's slots
     response named. */
  const autoProviderId = autoPick && selectedState ? nz(selectedState.serviceProviderId, null) : null;

  const chooseDate = (key) => {
    dateSeq.current += 1;
    setDateSel({ key, seq: dateSeq.current });
  };

  // ── bridge's handlers ───────────────────────────────────────────────
  /* resetValues(7): the chosen date, the slot cache and the slot go, the strip
     falls back to the provider's first bookable week, and `loading` drops. */
  const resetAfterProviderChange = () => {
    setAnchorKey(null);
    setDateSel(null);
    setSlotState({});
    setSelectedSlot(null);
    setProvidersLoading(false);
  };
  const onProviderChange = (next) => {
    if (next) {
      // A first pick from empty is NOT a reset in bridge — only a change is.
      if (provider != null) resetAfterProviderChange();
      setProvider(next);
    } else {
      setProvider(null);
      resetAfterProviderChange();
    }
  };
  /* The checkbox handler — clears the date, slot, cache and provider but NOT
     the calendar anchor (bridge does not call resetValues here). */
  const onToggleAuto = () => {
    setDateSel(null);
    setSelectedSlot(null);
    setSlotState({});
    setProvider(null);
    setAutoPick((v) => !v);
  };
  /* resetValues(2), then the type-2 provider fetch (the effect above). */
  const onLanguageChange = (next) => {
    setAnchorKey(null);
    setProvider(null);
    setAutoPick(false);
    setDateSel(null);
    setSlotState({});
    setSelectedSlot(null);
    setLanguageSelected(next);
  };

  // ── closing ─────────────────────────────────────────────────────────
  const requestClose = useCallback(() => {
    if (busy) {
      dismissedRef.current = true;
      setDismissed(true);
      return;
    }
    onClose();
  }, [busy, onClose]);

  // ── focus trap / scroll lock / Escape ───────────────────────────────
  const prevOverflow = useRef("");
  useEffect(() => {
    lastFocused.current = document.activeElement;
    prevOverflow.current = document.body.style.overflow;
    return () => {
      document.body.style.overflow = prevOverflow.current;
      if (lastFocused.current && lastFocused.current.focus) lastFocused.current.focus();
    };
  }, []);
  const hidden = dismissed && !confirmed;
  useEffect(() => {
    document.body.style.overflow = hidden ? prevOverflow.current : "hidden";
    if (!hidden && panelRef.current) panelRef.current.focus();
  }, [hidden, confirmed]);

  /* Capture phase: a child menu closes itself from a bubbling handler, which
     unmounts it synchronously — a bubbling listener here would see no menu
     and close the whole dialog on the same Escape. */
  useEffect(() => {
    const onKey = (e) => {
      if (hidden) return;
      if (e.key === "Escape") {
        if (!popoverOpenRef.current) (confirmed ? onClose : requestClose)();
        return;
      }
      if (e.key !== "Tab" || !panelRef.current) return;
      const focusable = panelRef.current.querySelectorAll(
        'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [hidden, confirmed, onClose, requestClose]);

  const handlePopover = useCallback((isOpen) => {
    popoverOpenRef.current = isOpen;
  }, []);

  /* The first step the move is still missing, in the order the dialog asks for
     them — "" once it can be sent. The same checks as submit's, which stay as the
     last line of defence. */
  const missingStep =
    reason == null
      ? "Select a reason for rescheduling"
      : reason === RESCHEDULE_OTHER_REASON && comment.trim() === ""
      ? "Add a comment for the other reason"
      : provider == null && autoPick === false
      ? hideProviderBlock
        ? "This appointment's service provider is not available to reschedule"
        : "Select a service provider"
      : selectedDate == null || !isBookable(selectedDate)
      ? "Select a date"
      : selectedSlot == null
      ? "Select a time slot"
      : autoPick && !autoProviderId
      ? "No service provider was returned for this slot"
      : "";
  const canSubmit = !loading && missingStep === "";

  // ── submit — bridge's rescheduleAppointment ─────────────────────────
  const submit = () => {
    if (busy) return undefined;
    if (reason == null) return toast("Please select a reason for rescheduling");
    if (reason === RESCHEDULE_OTHER_REASON && comment.trim() === "") {
      return toast("Please add a comment for the other reason");
    }
    if (provider == null && autoPick === false) return toast("Please select a service provider");
    if (selectedDate == null || !isBookable(selectedDate)) return toast("Please select a date");
    if (selectedSlot == null) return toast("Please select a time slot");
    const providerId = autoPick ? autoProviderId : provider.id;
    if (autoPick && !providerId) {
      return toast("No service provider was returned for this slot. Pick one from the list instead.");
    }

    setBusy(true);
    const body = {
      appointmentID: appointment.id,
      appointmentDate: `${selectedDate}T00:00:00.000Z`,
      timeslot: selectedSlot.value,
      timeSlotId: selectedSlot.key,
      serviceProviderID: providerId,
      reason: reason === RESCHEDULE_OTHER_REASON ? comment.trim() : reason,
    };
    // Captured now, for the success dialog.
    const summary = `${longWithDow(selectedDate)} · ${slotLabel(selectedSlot)}`;
    service.rescheduleAppointment(
      body,
      () => {
        if (!mounted.current) return;
        setBusy(false);
        dismissedRef.current = false;
        setDismissed(false);
        setConfirmed(summary);
        if (onRescheduled) onRescheduled();
      },
      (err) => {
        if (!mounted.current) return;
        setBusy(false);
        toastErr(err);
        if (dismissedRef.current) onClose();
      }
    );
    return undefined;
  };

  // eslint-disable-next-line eqeqeq
  const pidValue = appointment.bookedFor == 0 ? appointment.patientID : appointment.bookedFor;
  const currentWhen = currentlyScheduled(
    appointment.appointmentDate,
    appointment.timeSlot,
    appointment.serviceDuration
  );
  const providerLabel = useCallback(
    (o) =>
      `(${o.id}) ${o.name}${
        typeId === SERVICE_TYPE.HEALTH_CHECKUP
          ? ` -${o.address ? o.address.city : undefined}-${o.address ? o.address.landmark : undefined}`
          : ""
      }`,
    [typeId]
  );

  if (hidden) return null;

  return ReactDOM.createPortal(
    <div
      className="rs-overlay"
      onMouseDown={(e) => {
        if (e.target !== e.currentTarget || popoverOpenRef.current) return;
        if (confirmed) onClose();
        else requestClose();
      }}
    >
      {confirmed ? (
        <div
          className="rs-modal is-confirmed"
          role="dialog"
          aria-modal="true"
          aria-label="Appointment rescheduled successfully"
          ref={panelRef}
          tabIndex={-1}
        >
          <span className="rs-done-check" aria-hidden="true">
            <CheckBig />
          </span>
          <h2 className="rs-done-title">Appointment rescheduled successfully</h2>
          <p className="rs-done-when">{confirmed}</p>
          <button type="button" className="rs-btn rs-btn-primary rs-done-btn" onClick={onClose}>
            Done
          </button>
        </div>
      ) : (
        <div
          className="rs-modal"
          role="dialog"
          aria-modal="true"
          aria-label="Reschedule appointment?"
          ref={panelRef}
          tabIndex={-1}
        >
          <div className="rs-head">
            <h2 className="rs-title">Reschedule appointment?</h2>
            <button type="button" className="rs-close" aria-label="Close" onClick={requestClose}>
              &times;
            </button>
          </div>

          <div className="rs-body">
            <div className="rs-appt">
              <span className="rs-appt-name">
                {`${nz(appointment.patientName, "")} · PID ${nz(pidValue, "")}`}
              </span>
              {currentWhen ? (
                <span className="rs-appt-when">{`Currently scheduled · ${currentWhen}`}</span>
              ) : null}
            </div>

            <p className="rs-question">
              Are you sure you want to reschedule this appointment? Please add a reason below.
              <span className="rs-req"> *</span>
            </p>
            <div className="rs-reasons">
              {[...reasons, RESCHEDULE_OTHER_REASON].map((r, i) => (
                <button
                  key={`${i}-${r}`}
                  type="button"
                  className={`rs-reason${reason === r ? " is-selected" : ""}`}
                  aria-pressed={reason === r}
                  onClick={() => {
                    setReason(r);
                    if (r !== RESCHEDULE_OTHER_REASON) setComment("");
                  }}
                >
                  {reason === r && (
                    <span className="rs-reason-check" aria-hidden="true">
                      <CheckMini />
                    </span>
                  )}
                  {r}
                </button>
              ))}
            </div>
            {reason === RESCHEDULE_OTHER_REASON && (
              <textarea
                className="rs-comment"
                placeholder="Add a comment..."
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
            )}

            {showLanguage && (
              <div className="rs-field">
                <label className="rs-label" htmlFor="rs-language">Language</label>
                <LanguageMulti
                  options={languages}
                  value={languageSelected}
                  onChange={onLanguageChange}
                  onOpenChange={handlePopover}
                />
              </div>
            )}

            {!hideProviderBlock && (
              <div className="rs-field">
                <label className="rs-check-row">
                  <input
                    type="checkbox"
                    checked={autoPick}
                    disabled={!MODES_WITH_AUTO_SP.includes(mode) || isHomeSample || loading}
                    onChange={onToggleAuto}
                  />
                  <span>
                    Select service provider or check mark to select automatically
                    <span className="rs-req"> *</span>
                  </span>
                </label>
                <ProviderPicker
                  id="rs-provider"
                  options={providers}
                  value={provider}
                  disabled={autoPick || isHomeSample || loading}
                  getLabel={providerLabel}
                  getSearch={providerLabel}
                  onChange={onProviderChange}
                  onOpenChange={handlePopover}
                />
              </div>
            )}

            <span className="rs-label">New date</span>
            <div className="rs-strip">
              {stripDays.map((d) => {
                const st = slotState[d.key];
                const isSelected = selectedDate === d.key;
                const hasError = st != null && st.error != null;
                /* A day the server answered with NO slots is as unbookable as a day
                   outside the window, so it is disabled the same way. A day whose
                   slots FAILED to load stays clickable — the click retries it. */
                const noSlots = !hasError && st != null && st.loading !== true && st.slots.length === 0;
                const muted = hasError || noSlots;
                return (
                  <button
                    key={d.key}
                    type="button"
                    className={`rs-day${isSelected ? " is-selected" : ""}${muted ? " is-empty" : ""}`}
                    disabled={!isProviderChosen || !isBookable(d.key) || noSlots}
                    onClick={() => {
                      /* An errored date stays clickable so the failure can be
                         retried (and re-surfaced as a toast). */
                      if (hasError) {
                        fetchSlots([d.key]);
                        return;
                      }
                      chooseDate(d.key);
                    }}
                  >
                    <span className="rs-day-dow">{d.dow}</span>
                    <span className="rs-day-dom">{d.dom}</span>
                    <span className="rs-day-status">{st && st.loading === true ? <Spinner /> : null}</span>
                  </button>
                );
              })}
              <StripCalendar
                minKey={minKey}
                maxKey={maxKey}
                isDayBookable={autoWindows ? isBookable : undefined}
                selectedKey={selectedDate}
                disabled={loading || !isProviderChosen}
                onPick={(key) => {
                  setAnchorKey(key);
                  chooseDate(key);
                }}
                onOpenChange={handlePopover}
              />
            </div>
            <p className="rs-hint">Grey dates have no free slots · calendar opens any future date</p>

            <span className="rs-label">
              {selectedDate == null ? "Available slots" : `Available slots · ${shortWithDow(selectedDate)}`}
            </span>
            <div className="rs-slots">
              {selectedDate == null ? (
                <span className="rs-slots-msg">Select a date to see the available slots</span>
              ) : selectedState == null || selectedState.loading === true ? (
                <span className="rs-slots-loader">
                  <Spinner large />
                </span>
              ) : selectedState.error != null ? (
                <span className="rs-slots-msg">Could not load slots for this date</span>
              ) : selectedState.slots.length === 0 ? (
                <span className="rs-slots-msg">No slots available on this date</span>
              ) : (
                selectedState.slots.map((s) => (
                  <button
                    key={s.key}
                    type="button"
                    className={`rs-slot${selectedSlot && selectedSlot.key === s.key ? " is-selected" : ""}`}
                    onClick={() => setSelectedSlot(s)}
                  >
                    {slotLabel(s)}
                  </button>
                ))
              )}
            </div>

            {missingStep && !loading ? (
              <p className="rs-next-step" id="rs-next-step" aria-live="polite">
                {missingStep}
              </p>
            ) : null}
            <div className="rs-actions">
              <button type="button" className="rs-btn rs-btn-ghost" onClick={requestClose}>
                No, go back
              </button>
              <button
                type="button"
                className="rs-btn rs-btn-primary"
                onClick={submit}
                disabled={!canSubmit}
                title={missingStep || undefined}
                aria-describedby={missingStep ? "rs-next-step" : undefined}
              >
                Yes, reschedule
              </button>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
};

RescheduleModal.propTypes = {
  /** The appointment being moved — every value the dialog shows or sends. */
  appointment: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  /** Called after a successful move, for the host to re-read the appointment. */
  onRescheduled: PropTypes.func,
};

export default RescheduleModal;
